import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import LayoutWrapper from '../../components/Layout/LayoutWrapper';
import { LoadingSpinner, ErrorHandler } from '../../components/common/ErrorHandler';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ArrowLeft, Save, UserPlus, Trash2, Camera } from 'lucide-react';
import useToast from '../../hooks/useToast';
import { generateGuestReceipt, generateMembershipForm } from '../../components/prints/clubPrintTemplates';
import { getImageUrl } from '../../utils/image';
import config from '../../config';



// Helper function to calculate age from DOB
const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const MembersForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const isEdit = !!id;
  const { showSuccess, showError } = useToast();
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [membershipType, setMembershipType] = useState(typeParam === 'guest' ? 'Guest' : 'Permanent');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    status: 'Active',
    // Guest fields
    guest_name: '',
    member_reference_name: '',
    amount: '',
    remarks: '',
    // Member fields
    applicant_name: '',
    father_name: '',
    mobile: '',
    dob: '',
    sex: '',
    marital_status: '',
    nationality: 'Indian',
    email: '',
    aadhaar: '',
    pan: '',
    qualification: '',
    organization: '',
    designation: '',
    address: '',
    profile_picture: '',
    // Spouse
    spouse_name: '',
    spouse_dob: '',
    spouse_qualification: '',
    spouse_occupation: '',
    // Financial
    membership_fee: '',
    opening_balance: '',
    // Subscription (temporary)
    subscription_from: '',
    subscription_to: '',
    // Games
    games_of_interest: [],
    // Dependants
    dependants: []
  });

  useEffect(() => {
    if (isEdit) {
      fetchMember();
    }
  }, [id]);

  const fetchMember = async () => {
    try {
      setLoading(true);
      const endpoint = typeParam === 'guest' ? `/api/guests/${id}` : `/api/members/${id}`;
      const response = await api.get(endpoint);
      const data = response.data;

      if (typeParam === 'guest') {
        setMembershipType('Guest');
      } else {
        setMembershipType(data.membership_type || 'Permanent');
      }

      setFormData(prev => ({
  ...prev,
  ...data,
  profile_picture: data.profile_picture || '',

  amount: data.amount?.toString() || '',
  membership_fee: data.membership_fee?.toString() || '',
  opening_balance: data.opening_balance?.toString() || '',
  games_of_interest: data.games_of_interest || [],
  dependants: data.dependants || []
}));

      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  if (file.size > 2 * 1024 * 1024) {
    showError('Image size must be less than 2MB');
    return;
  }

  if (formData.profile_picture?.startsWith('blob:')) {
  URL.revokeObjectURL(formData.profile_picture);
}

const previewUrl = URL.createObjectURL(file);


  setPhotoFile(file);
  setFormData(prev => ({
    ...prev,
    profile_picture: previewUrl
  }));
};

// cleanup
useEffect(() => {

  return () => {
    if (formData.profile_picture?.startsWith('blob:')) {
      URL.revokeObjectURL(formData.profile_picture);
    }
  };
}, [formData.profile_picture]);


  const handleGameToggle = (game) => {
    const current = formData.games_of_interest || [];
    if (current.includes(game)) {
      handleChange('games_of_interest', current.filter(g => g !== game));
    } else {
      handleChange('games_of_interest', [...current, game]);
    }
  };

  const addDependant = () => {
    handleChange('dependants', [
      ...formData.dependants,
      { name: '', sex: '', relation: '', dob: '', occupation: '' }
    ]);
  };

  const removeDependant = (index) => {
    const updated = formData.dependants.filter((_, i) => i !== index);
    handleChange('dependants', updated);
  };

  const updateDependant = (index, field, value) => {
    const updated = formData.dependants.map((dep, i) =>
      i === index ? { ...dep, [field]: value } : dep
    );
    handleChange('dependants', updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (membershipType === 'Guest') {
      if (!formData.guest_name || !formData.guest_name.trim()) {
        showError('Guest name is required');
        return;
      }
      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        showError('Valid amount is required');
        return;
      }
    } else {
      if (!formData.applicant_name || !formData.applicant_name.trim()) {
        showError('Applicant name is required');
        return;
      }
      if (!formData.mobile || !formData.mobile.trim()) {
        showError('Mobile number is required');
        return;
      }
      if (formData.mobile && !/^[0-9]{10}$/.test(formData.mobile.replace(/\s/g, ''))) {
        showError('Please enter a valid 10-digit mobile number');
        return;
      }
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        showError('Please enter a valid email address');
        return;
      }
      if (membershipType === 'Temporary') {
        if (!formData.subscription_from || !formData.subscription_to) {
          showError('Subscription dates are required for temporary members');
          return;
        }
        if (new Date(formData.subscription_from) > new Date(formData.subscription_to)) {
          showError('Subscription "From" date must be before "To" date');
          return;
        }
      }
    }

    try {
      setSaving(true);

      let payload;
      let endpoint;

      if (membershipType === 'Guest') {
        payload = {
          date: formData.date,
          guest_name: formData.guest_name.trim(),
          member_reference_name: formData.member_reference_name?.trim() || '',
          amount: parseFloat(formData.amount) || 0,
          remarks: formData.remarks?.trim() || '',
          status: formData.status
        };
        endpoint = isEdit ? `/api/guests/${id}` : '/api/guests';
      } else {
        payload = {
          membership_type: membershipType,
          date: formData.date,
          status: formData.status,
          applicant_name: formData.applicant_name.trim(),
          father_name: formData.father_name?.trim() || '',
          mobile: formData.mobile.trim(),
          dob: formData.dob || null,
          sex: formData.sex || '',
          marital_status: formData.marital_status || '',
          nationality: formData.nationality || 'Indian',
          email: formData.email?.trim() || '',
          aadhaar: formData.aadhaar?.trim() || '',
          pan: formData.pan?.trim() || '',
          qualification: formData.qualification?.trim() || '',
          organization: formData.organization?.trim() || '',
          designation: formData.designation?.trim() || '',
          address: formData.address?.trim() || '',
          spouse_name: formData.spouse_name?.trim() || '',
          spouse_dob: formData.spouse_dob || null,
          spouse_qualification: formData.spouse_qualification?.trim() || '',
          spouse_occupation: formData.spouse_occupation?.trim() || '',
          membership_fee: parseFloat(formData.membership_fee) || 0,
          opening_balance: parseFloat(formData.opening_balance) || 0,
          subscription_from: formData.subscription_from || null,
          subscription_to: formData.subscription_to || null,
          games_of_interest: formData.games_of_interest || [],
          dependants: formData.dependants || []
        };
        endpoint = isEdit ? `/api/members/${id}` : '/api/members';
      }

      const response = isEdit
        ? await api.put(endpoint, payload)
        : await api.post(endpoint, payload);

      showSuccess(isEdit ? 'Member updated successfully' : 'Member saved successfully');
       
      const savedMember = response.data;

if (photoFile && membershipType !== 'Guest') {
  const photoFormData = new FormData();
  photoFormData.append('file', photoFile);

  await api.post(
    `/api/members/${savedMember.id}/photo`,
    photoFormData
  );

  setPhotoFile(null); // ✅ add this
}



      // Ask user if they want to print (only for new members, not edits)
      if (!isEdit) {
        const shouldPrint = window.confirm('Do you want to print now?');

        if (shouldPrint) {
          try {
            if (membershipType === 'Guest') {
              const doc = generateGuestReceipt(response.data);
              doc.save(`guest_receipt_${response.data.membership_no}.pdf`);
            } else {
              const doc = generateMembershipForm(response.data);
              doc.save(`membership_form_${response.data.membership_no}.pdf`);
            }
          } catch (err) {
            showError('Failed to generate print');
          }
        }
      }

      navigate('/members');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to save member');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <LayoutWrapper title={isEdit ? 'Edit Member' : 'Add Member'}>
        <LoadingSpinner text="Loading member details..." />
      </LayoutWrapper>
    );
  }

  const memberAge = calculateAge(formData.dob);
  const profileImageSrc =
  formData.profile_picture?.startsWith('blob:')
    ? formData.profile_picture
    : getImageUrl(formData.profile_picture);

  return (
    <LayoutWrapper
      title={isEdit ? 'Edit Member' : 'Add New Member'}
      subtitle="Fill in the member details"
      actions={
        <Button variant="outline" onClick={() => navigate('/members')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Back to List</span>
          <span className="sm:hidden">Back</span>
        </Button>
      }
    >
      {error && <ErrorHandler error={error} onRetry={fetchMember} />}

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Membership Type Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Membership Type</CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={membershipType}
              onValueChange={setMembershipType}
              className="flex flex-col sm:flex-row gap-4 sm:gap-6"
              disabled={isEdit}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Permanent" id="permanent" data-testid="type-permanent" />
                <Label htmlFor="permanent" className="text-sm sm:text-base">Permanent</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Temporary" id="temporary" data-testid="type-temporary" />
                <Label htmlFor="temporary" className="text-sm sm:text-base">Temporary</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Guest" id="guest" data-testid="type-guest" />
                <Label htmlFor="guest" className="text-sm sm:text-base">Guest</Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Guest Form */}
        {membershipType === 'Guest' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Guest Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date" className="text-sm">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e.target.value)}
                    required
                    data-testid="guest-date"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="status" className="text-sm">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                    <SelectTrigger data-testid="guest-status" className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="guest_name" className="text-sm">Guest Name *</Label>
                  <Input
                    id="guest_name"
                    value={formData.guest_name}
                    onChange={(e) => handleChange('guest_name', e.target.value)}
                    required
                    data-testid="guest-name"
                    placeholder="Enter guest name"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="member_reference_name" className="text-sm">Member Reference Name</Label>
                  <Input
                    id="member_reference_name"
                    value={formData.member_reference_name}
                    onChange={(e) => handleChange('member_reference_name', e.target.value)}
                    data-testid="member-reference-name"
                    placeholder="Optional"
                    className="text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="amount" className="text-sm">Amount () *</Label>
                  <Input
                    id="amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => handleChange('amount', e.target.value)}
                    required
                    data-testid="guest-amount"
                    placeholder="0.00"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="remarks" className="text-sm">Remarks / Fee Type</Label>
                  <Input
                    id="remarks"
                    value={formData.remarks}
                    onChange={(e) => handleChange('remarks', e.target.value)}
                    placeholder="e.g., Daily Visit"
                    data-testid="guest-remarks"
                    className="text-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Permanent/Temporary Form */}
        {membershipType !== 'Guest' && (
          <>
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date" className="text-sm">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleChange('date', e.target.value)}
                      required
                      data-testid="member-date"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="status" className="text-sm">Status</Label>
                    <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                      <SelectTrigger data-testid="member-status" className="text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Photo Upload */}
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="relative">
                    {formData.profile_picture ? (
                    <img
  src={profileImageSrc}
  alt="Profile"
  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-gray-200"
  onError={(e) => {
    e.currentTarget.src = '';
  }}
/>


                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                        <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                      </div>
                    )}
                    <input
  type="file"
  accept="image/png,image/jpeg,image/jpg,image/webp"
  onChange={handleImageUpload}
  className="absolute inset-0 opacity-0 cursor-pointer"
/>

                  </div>
                  <div className="text-center sm:text-left">
                    <p className="font-medium text-sm sm:text-base">Profile Picture</p>
                    <p className="text-xs sm:text-sm text-gray-500">Click to upload (Max 2MB)</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="applicant_name" className="text-sm">Applicant Name *</Label>
                    <Input
                      id="applicant_name"
                      value={formData.applicant_name}
                      onChange={(e) => handleChange('applicant_name', e.target.value)}
                      required
                      data-testid="applicant-name"
                      placeholder="Full name"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="father_name" className="text-sm">Father's Name</Label>
                    <Input
                      id="father_name"
                      value={formData.father_name}
                      onChange={(e) => handleChange('father_name', e.target.value)}
                      data-testid="father-name"
                      placeholder="Father's name"
                      className="text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="mobile" className="text-sm">Mobile *</Label>
                    <Input
                      id="mobile"
                      value={formData.mobile}
                      onChange={(e) => handleChange('mobile', e.target.value)}
                      required
                      data-testid="mobile"
                      placeholder="10-digit number"
                      maxLength="10"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      data-testid="email"
                      placeholder="email@example.com"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dob" className="text-sm">Date of Birth</Label>
                    <Input
                      id="dob"
                      type="date"
                      value={formData.dob}
                      onChange={(e) => handleChange('dob', e.target.value)}
                      data-testid="dob"
                      className="text-sm"
                    />
                    {memberAge !== null && (
                      <p className="text-xs sm:text-sm text-blue-600 mt-1 font-medium">Age: {memberAge} years</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="sex" className="text-sm">Sex</Label>
                    <Select value={formData.sex} onValueChange={(v) => handleChange('sex', v)}>
                      <SelectTrigger data-testid="sex" className="text-sm">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="marital_status" className="text-sm">Marital Status</Label>
                    <Select value={formData.marital_status} onValueChange={(v) => handleChange('marital_status', v)}>
                      <SelectTrigger data-testid="marital-status" className="text-sm">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Married">Married</SelectItem>
                        <SelectItem value="Unmarried">Unmarried</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="nationality" className="text-sm">Nationality</Label>
                    <Select value={formData.nationality} onValueChange={(v) => handleChange('nationality', v)}>
                      <SelectTrigger data-testid="nationality" className="text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Indian">Indian</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="aadhaar" className="text-sm">Aadhaar Number</Label>
                    <Input
                      id="aadhaar"
                      value={formData.aadhaar}
                      onChange={(e) => handleChange('aadhaar', e.target.value)}
                      data-testid="aadhaar"
                      placeholder="12-digit number"
                      maxLength="12"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pan" className="text-sm">PAN Number</Label>
                    <Input
                      id="pan"
                      value={formData.pan}
                      onChange={(e) => handleChange('pan', e.target.value.toUpperCase())}
                      data-testid="pan"
                      placeholder="ABCDE1234F"
                      maxLength="10"
                      className="text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="qualification" className="text-sm">Qualification</Label>
                    <Input
                      id="qualification"
                      value={formData.qualification}
                      onChange={(e) => handleChange('qualification', e.target.value)}
                      data-testid="qualification"
                      placeholder="Educational qualification"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="organization" className="text-sm">Organization</Label>
                    <Input
                      id="organization"
                      value={formData.organization}
                      onChange={(e) => handleChange('organization', e.target.value)}
                      data-testid="organization"
                      placeholder="Company/Organization"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="designation" className="text-sm">Designation</Label>
                    <Input
                      id="designation"
                      value={formData.designation}
                      onChange={(e) => handleChange('designation', e.target.value)}
                      data-testid="designation"
                      placeholder="Job title"
                      className="text-sm"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address" className="text-sm">Residential Address</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    rows={3}
                    data-testid="address"
                    placeholder="Complete residential address"
                    className="text-sm"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Spouse Information */}
            {formData.marital_status === 'Married' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg">Spouse Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="spouse_name" className="text-sm">Spouse Name</Label>
                      <Input
                        id="spouse_name"
                        value={formData.spouse_name}
                        onChange={(e) => handleChange('spouse_name', e.target.value)}
                        data-testid="spouse-name"
                        placeholder="Spouse full name"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="spouse_dob" className="text-sm">Spouse DOB</Label>
                      <Input
                        id="spouse_dob"
                        type="date"
                        value={formData.spouse_dob}
                        onChange={(e) => handleChange('spouse_dob', e.target.value)}
                        data-testid="spouse-dob"
                        className="text-sm"
                      />
                      {formData.spouse_dob && (
                        <p className="text-xs sm:text-sm text-blue-600 mt-1 font-medium">
                          Age: {calculateAge(formData.spouse_dob)} years
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="spouse_qualification" className="text-sm">Spouse Qualification</Label>
                      <Input
                        id="spouse_qualification"
                        value={formData.spouse_qualification}
                        onChange={(e) => handleChange('spouse_qualification', e.target.value)}
                        data-testid="spouse-qualification"
                        placeholder="Educational qualification"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="spouse_occupation" className="text-sm">Spouse Occupation</Label>
                      <Input
                        id="spouse_occupation"
                        value={formData.spouse_occupation}
                        onChange={(e) => handleChange('spouse_occupation', e.target.value)}
                        data-testid="spouse-occupation"
                        placeholder="Job/Occupation"
                        className="text-sm"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Subscription Period (Temporary only) */}
            {membershipType === 'Temporary' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg">Subscription Period</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="subscription_from" className="text-sm">Subscription From *</Label>
                      <Input
                        id="subscription_from"
                        type="date"
                        value={formData.subscription_from}
                        onChange={(e) => handleChange('subscription_from', e.target.value)}
                        required
                        data-testid="subscription-from"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subscription_to" className="text-sm">Subscription To *</Label>
                      <Input
                        id="subscription_to"
                        type="date"
                        value={formData.subscription_to}
                        onChange={(e) => handleChange('subscription_to', e.target.value)}
                        required
                        data-testid="subscription-to"
                        className="text-sm"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Financial Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Financial Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="membership_fee" className="text-sm">Membership Fee ()</Label>
                    <Input
                      id="membership_fee"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.membership_fee}
                      onChange={(e) => handleChange('membership_fee', e.target.value)}
                      data-testid="membership-fee"
                      placeholder="0.00"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="opening_balance" className="text-sm">Opening Balance ()</Label>
                    <Input
                      id="opening_balance"
                      type="number"
                      step="0.01"
                      value={formData.opening_balance}
                      onChange={(e) => handleChange('opening_balance', e.target.value)}
                      data-testid="opening-balance"
                      placeholder="0.00"
                      className="text-sm"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Games of Interest */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Games of Interest</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                  {config.gamesOfInterest.map((game) => (
                    <div key={game} className="flex items-center space-x-2">
                      <Checkbox
                        id={game}
                        checked={formData.games_of_interest?.includes(game)}
                        onCheckedChange={() => handleGameToggle(game)}
                        data-testid={`game-${game.toLowerCase().replace(' ', '-')}`}
                      />
                      <Label htmlFor={game} className="text-xs sm:text-sm">{game}</Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Dependants */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="text-base sm:text-lg">Dependants</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addDependant} data-testid="add-dependant-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Add Dependant</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </CardHeader>
              <CardContent>
                {formData.dependants.length === 0 ? (
                  <p className="text-gray-500 text-center py-4 text-sm">No dependants added</p>
                ) : (
                  <div className="space-y-4">
                    {formData.dependants.map((dep, index) => {
                      const depAge = calculateAge(dep.dob);
                      return (
                        <div key={index} className="p-3 sm:p-4 border rounded-lg relative" data-testid={`dependant-${index}`}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute top-2 right-2 text-red-500 h-8 w-8"
                            onClick={() => removeDependant(index)}
                            data-testid={`remove-dependant-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 pr-8">
                            <div>
                              <Label className="text-xs">Name</Label>
                              <Input
                                value={dep.name}
                                onChange={(e) => updateDependant(index, 'name', e.target.value)}
                                data-testid={`dependant-name-${index}`}
                                placeholder="Name"
                                className="mt-1 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Sex</Label>
                              <Select
  value={dep.sex}
  onValueChange={(v) => updateDependant(index, 'sex', v)}
>
  <SelectTrigger
    data-testid={`dependant-sex-${index}`}
    className="
      mt-1 h-9
      bg-white
      border border-gray-300
      text-sm
      focus:ring-2 focus:ring-blue-300
    "
  >
    <SelectValue placeholder="Select" />
  </SelectTrigger>

  <SelectContent
    forceMount
    className="
      bg-white
      border border-gray-300
      shadow-xl
      rounded-md
      z-50
    "
  >
    <SelectItem value="Male" className="text-sm hover:bg-gray-100">
      Male
    </SelectItem>
    <SelectItem value="Female" className="text-sm hover:bg-gray-100">
      Female
    </SelectItem>
  </SelectContent>
</Select>

                            </div>
                            <div>
                              <Label className="text-xs">Relation</Label>
                              <Input
                                value={dep.relation}
                                onChange={(e) => updateDependant(index, 'relation', e.target.value)}
                                data-testid={`dependant-relation-${index}`}
                                placeholder="Relation"
                                className="mt-1 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">DOB</Label>
                              <Input
                                type="date"
                                value={dep.dob}
                                onChange={(e) => updateDependant(index, 'dob', e.target.value)}
                                data-testid={`dependant-dob-${index}`}
                                className="mt-1 text-sm"
                              />
                              {depAge !== null && (
                                <p className="text-xs text-blue-600 mt-1 font-medium">Age: {depAge} years</p>
                              )}
                            </div>
                            <div>
                              <Label className="text-xs">Occupation</Label>
                              <Input
                                value={dep.occupation}
                                onChange={(e) => updateDependant(index, 'occupation', e.target.value)}
                                data-testid={`dependant-occupation-${index}`}
                                placeholder="Occupation"
                                className="mt-1 text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Submit Button */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 sticky bottom-0 bg-white p-4 border-t sm:static sm:border-0 sm:p-0 z-10">
          <Button type="button" variant="outline" onClick={() => navigate('/members')} data-testid="cancel-btn" className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button type="submit" disabled={saving} data-testid="save-member-btn" className="w-full sm:w-auto">
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEdit ? 'Update Member' : 'Save Member'}
              </>
            )}
          </Button>
        </div>
      </form>
    </LayoutWrapper>
  );
};

export default MembersForm;
